import * as React from 'react';
import validator, {
  Rule,
  RulesMessages,
  ValidationResponse
} from '../utils/validator';
import RadioInput from './RadioInput';
import '../css/form-input-core.sass';
import '../css/form-group-error.sass';

export interface RadioOptions {
  label: string;
  description?: React.ReactNode;
  value: string;
  focus?: boolean;
  hideRadio?: boolean;
  disabled?: boolean;
}

interface RadioGroupInputProps {
  name: string;
  labelOnBottom?: boolean;
  value?: string;
  id: string;
  label: string;
  options?: RadioOptions[];
  children?: React.ReactElement[];
  required?: boolean;
  hasError?: boolean;
  errors?: any;
  validationRules?: Rule[];
  rulesMessages?: RulesMessages;
  onChange?: Function;
  onComplete?: Function;
  onError?: Function;
  onBlur?: Function;
  theme?: 'night' | 'day';
  alignment?: 'vertical' | 'horizontal';
  wrapAt?: number;
  disabled?: boolean;
}

interface RadioGroupInputState {
  inputValue?: string;
  validationResponse?: ValidationResponse;
}

export default function RadioGroupInput(props: RadioGroupInputProps) {
  const [state, setState] = React.useState<RadioGroupInputState>({
    inputValue: props.value || ''
  });
  const [options, setOptions] = React.useState<RadioOptions[]>(
    props.options || []
  );

  const { alignment = 'vertical', wrapAt = 2 } = props;

  const componentState = { state, setState };

  const componentPropsRules: Rule[] = [];
  if (props.validationRules) {
    componentPropsRules.push(...props.validationRules);
  }
  if (
    props.required &&
    !componentPropsRules.some((rule) => rule.ruleName === 'required')
  ) {
    componentPropsRules.push({
      ruleName: 'required',
      message: props.rulesMessages && props.rulesMessages.required,
      defaultMessage: `Please select ${props.label}.`
    });
  }

  const componentValidator = {
    validationRules: [
      {
        fieldName: 'inputValue',
        rules: componentPropsRules
      }
    ]
  };

  React.useEffect(() => {
    if (
      !!state.inputValue &&
      validator.validateComponent(
        componentState,
        componentValidator,
        null,
        true
      )
    ) {
      if (props.onComplete !== undefined) {
        props.onComplete(state.inputValue);
      }
    }
  }, [state.inputValue]);

  React.useEffect(() => {
    if (state.inputValue !== props.value) {
      setState({
        ...state,
        inputValue: props.value
      });
    }
  }, [props.value]);

  React.useEffect(() => {
    if (options && options.length > 0 && options !== props.options) {
      setOptions(props.options || []);
    }
  }, [props.options]);

  const radioGroup: any = [];
  options.forEach((option, index) => {
    const marginLeft = (index + 1) % wrapAt === 1 ? 0 : 8;
    const marginRight = (index + 1) % wrapAt === 0 ? 0 : 8;
    const style =
      props.alignment === 'horizontal'
        ? {
            flexBasis: `calc(${100 / wrapAt}% - ${marginLeft + marginRight}px)`,
            marginLeft: `${marginLeft}px`,
            marginRight: `${marginRight}px`
          }
        : undefined;

    radioGroup.push(
      <RadioInput
        name={props.name}
        labelOnBottom={props.labelOnBottom}
        key={`${props.id}-${option.value}`}
        theme={props.theme}
        style={style}
        onChange={(e: any) => {
          setState({ ...state, inputValue: e.target.value });
          if (props.onChange !== undefined) {
            props.onChange(e);
          }
        }}
        onBlur={(e: any) => {
          if (validator.validateComponent(componentState, componentValidator)) {
            if (props.onComplete !== undefined) {
              props.onComplete(state.inputValue);
            }
          } else if (props.onError !== undefined) {
            props.onError();
          }
          if (props.onBlur !== undefined) {
            props.onBlur(e);
          }
        }}
        focus={option.focus}
        description={option.description}
        label={option.label}
        value={option.value}
        id={`${props.id}-${index}`}
        checked={state.inputValue === option.value}
        hideRadio={option.hideRadio}
        disabled={props.disabled || option.disabled}
      />
    );
  });

  const finalRadioGroup =
    alignment === 'horizontal' ? (
      <div
        style={{
          display: 'flex',
          flexWrap: options.length > wrapAt ? 'wrap' : undefined
        }}
        className="rdgroupHor"
      >
        {radioGroup}
      </div>
    ) : (
      radioGroup
    );

  React.useEffect(() => {
    if (!props.options && props.children) {
      const newOpts: RadioOptions[] = [];
      props.children.forEach((child) => {
        if (child.props.label && child.props.value) {
          newOpts.push({
            label: child.props.label,
            description: child.props.description,
            value: child.props.value,
            hideRadio: child.props.hideRadio,
            disabled: child.props.disabled
          });
        }
      });
      setOptions(newOpts);
    }
  }, []);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {(!validator.isAllValid(componentState) &&
        validator.getErrors(componentState)) ||
      (props.hasError && props.errors) ? (
        <div className="group-error">
          <div className="contextual-icon" />
          {(!validator.isAllValid(componentState) &&
            validator.getErrors(componentState)) ||
            (props.hasError && props.errors)}
          <div className="error-band" />
          {finalRadioGroup}
        </div>
      ) : (
        finalRadioGroup
      )}
    </>
  );
}
